import React, { useState, useEffect, useCallback } from 'react';
import { Server, Plus, Trash2, Play, Square, RefreshCw, ChevronDown, ChevronRight, CheckCircle, XCircle, Loader, ShieldAlert } from 'lucide-react';
import * as adminService from '../services/adminService';

const AdminSshPoolsPage = () => {
  const [pools, setPools] = useState({});
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [expandedPool, setExpandedPool] = useState(null);
  const [deploying, setDeploying] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newPool, setNewPool] = useState({ name: '', hosts: '', user: '', identity_file: '/root/.ssh/id_ed25519' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchPools = useCallback(async () => {
    try {
      const res = await adminService.getSshNodePools();
      if (res.success) {
        setPools(res.pools || {});
        const names = Object.keys(res.pools || {});
        const statusResults = {};
        await Promise.all(names.map(async (name) => {
          try {
            const s = await adminService.getSshNodePoolStatus(name);
            if (s.success) statusResults[name] = s;
          } catch {}
        }));
        setStatuses(statusResults);
      } else if (res.forbidden) {
        setForbidden(true);
      }
    } catch (err) {
      console.error('Failed to fetch SSH pools:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPools(); }, [fetchPools]);

  const handleAdd = async () => {
    if (!newPool.name.trim() || !newPool.hosts.trim() || !newPool.user.trim()) {
      setError('Name, hosts, and user are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const hosts = newPool.hosts.split(',').map(h => h.trim()).filter(Boolean);
      const config = {
        hosts,
        user: newPool.user.trim(),
        identity_file: newPool.identity_file.trim() || '/root/.ssh/id_ed25519',
      };
      const res = await adminService.updateSshNodePool(newPool.name.trim(), config);
      if (res.success) {
        setShowAdd(false);
        setNewPool({ name: '', hosts: '', user: '', identity_file: '/root/.ssh/id_ed25519' });
        await fetchPools();
      } else {
        setError(res.error || 'Failed to add pool');
      }
    } catch (err) {
      setError('Failed to add pool');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete SSH node pool "${name}"? This will not tear down any deployed cluster.`)) return;
    try {
      const res = await adminService.deleteSshNodePool(name);
      if (res.success) {
        await fetchPools();
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleDeploy = async (name) => {
    setDeploying(prev => ({ ...prev, [name]: 'deploying' }));
    try {
      const res = await adminService.deploySshNodePool(name);
      if (res.success) {
        setDeploying(prev => ({ ...prev, [name]: 'started' }));
        setTimeout(async () => {
          const s = await adminService.getSshNodePoolStatus(name);
          if (s.success) setStatuses(prev => ({ ...prev, [name]: s }));
          setDeploying(prev => ({ ...prev, [name]: null }));
        }, 30000);
      } else {
        setDeploying(prev => ({ ...prev, [name]: null }));
        setError(res.error || 'Deploy failed');
      }
    } catch {
      setDeploying(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleTeardown = async (name) => {
    if (!window.confirm(`Tear down the cluster runtime on "${name}"? Running workloads will be terminated.`)) return;
    setDeploying(prev => ({ ...prev, [name]: 'tearing-down' }));
    try {
      const res = await adminService.teardownSshNodePool(name);
      if (res.success) {
        setTimeout(async () => {
          const s = await adminService.getSshNodePoolStatus(name);
          if (s.success) setStatuses(prev => ({ ...prev, [name]: s }));
          setDeploying(prev => ({ ...prev, [name]: null }));
        }, 15000);
      } else {
        setDeploying(prev => ({ ...prev, [name]: null }));
      }
    } catch {
      setDeploying(prev => ({ ...prev, [name]: null }));
    }
  };

  const refreshStatus = async (name) => {
    try {
      const s = await adminService.getSshNodePoolStatus(name);
      if (s.success) setStatuses(prev => ({ ...prev, [name]: s }));
    } catch {}
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading SSH node pools...</p>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <ShieldAlert size={40} className="mx-auto text-gray-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Admins only</h2>
        <p className="text-sm text-gray-500">This page is restricted to administrators.</p>
      </div>
    );
  }

  const poolNames = Object.keys(pools);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Server size={22} /> SSH Node Pools
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage on-premise GPU machines accessible via SSH. Deploy the cluster runtime so they can run TCF workloads.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPools}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <Plus size={14} /> Add Node Pool
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 ml-2">&times;</button>
        </div>
      )}

      {showAdd && (
        <div className="bg-white rounded-lg shadow border p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Add SSH Node Pool</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Pool Name</label>
              <input
                type="text"
                value={newPool.name}
                onChange={e => setNewPool(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. pop-os"
                className="w-full px-3 py-1.5 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">SSH User</label>
              <input
                type="text"
                value={newPool.user}
                onChange={e => setNewPool(prev => ({ ...prev, user: e.target.value }))}
                placeholder="e.g. tcf"
                className="w-full px-3 py-1.5 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Hosts (comma-separated IPs)</label>
              <input
                type="text"
                value={newPool.hosts}
                onChange={e => setNewPool(prev => ({ ...prev, hosts: e.target.value }))}
                placeholder="e.g. 192.168.1.59, 192.168.1.60"
                className="w-full px-3 py-1.5 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Identity File (on orchestrator)</label>
              <input
                type="text"
                value={newPool.identity_file}
                onChange={e => setNewPool(prev => ({ ...prev, identity_file: e.target.value }))}
                placeholder="/root/.ssh/id_ed25519"
                className="w-full px-3 py-1.5 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => { setShowAdd(false); setError(null); }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={saving}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Pool'}
            </button>
          </div>
        </div>
      )}

      {poolNames.length === 0 && !showAdd ? (
        <div className="bg-white rounded-lg shadow border p-8 text-center">
          <Server size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">No SSH node pools configured.</p>
          <p className="text-gray-400 text-xs mt-1">Add a node pool to connect on-premise GPU machines.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {poolNames.map(name => {
            const pool = pools[name];
            const status = statuses[name];
            const isReady = status?.status === 'Ready';
            const deployState = deploying[name];
            const isExpanded = expandedPool === name;

            return (
              <div key={name} className="bg-white rounded-lg shadow border">
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedPool(isExpanded ? null : name)}
                >
                  <div className="flex items-center gap-3">
                    <button className="text-gray-400">
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 text-sm">{name}</span>
                        {status && (
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                            isReady
                              ? 'bg-green-50 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {isReady ? <CheckCircle size={12} /> : <XCircle size={12} />}
                            {status.status}
                          </span>
                        )}
                        {deployState && (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                            <Loader size={12} className="animate-spin" />
                            {deployState === 'deploying' ? 'Deploying...' : deployState === 'tearing-down' ? 'Tearing down...' : 'Started'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {pool.user}@{pool.hosts?.join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => refreshStatus(name)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                      title="Refresh status"
                    >
                      <RefreshCw size={14} />
                    </button>
                    {!isReady && (
                      <button
                        onClick={() => handleDeploy(name)}
                        disabled={!!deployState}
                        className="p-1.5 text-green-500 hover:text-green-700 rounded hover:bg-green-50 disabled:opacity-50"
                        title="Deploy cluster runtime"
                      >
                        <Play size={14} />
                      </button>
                    )}
                    {isReady && (
                      <button
                        onClick={() => handleTeardown(name)}
                        disabled={!!deployState}
                        className="p-1.5 text-orange-500 hover:text-orange-700 rounded hover:bg-orange-50 disabled:opacity-50"
                        title="Tear down cluster"
                      >
                        <Square size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(name)}
                      className="p-1.5 text-red-400 hover:text-red-600 rounded hover:bg-red-50"
                      title="Delete pool config"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t px-4 py-3 bg-gray-50 space-y-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 text-xs">Hosts</span>
                        <div className="mt-1 space-y-1">
                          {pool.hosts?.map((host, i) => (
                            <div key={i} className="font-mono text-gray-800 text-xs bg-white px-2 py-1 rounded border">{host}</div>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="text-gray-500 text-xs">User</span>
                          <p className="font-mono text-gray-800 text-xs">{pool.user}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs">Identity File</span>
                          <p className="font-mono text-gray-800 text-xs">{pool.identity_file || 'default'}</p>
                        </div>
                      </div>
                    </div>
                    {status?.reason && (
                      <div className="mt-2">
                        <span className="text-gray-500 text-xs">Status Detail</span>
                        <p className="text-xs text-gray-600 mt-0.5 font-mono bg-white px-2 py-1.5 rounded border whitespace-pre-wrap">{status.reason}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSshPoolsPage;
